import { Button, Text } from "@mantine/core"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { OrderApi } from "../../apis"
import { IOrderPlus } from "../../models/Order"
import { translateBackendError } from "../delivery/translate-error"
import { message } from "../../utils/message"
import { OrderActionCard } from "../order/order-action-card"

export type BuyerBulkKind = "approve" | "reject" | "cancel" | "review"

/** The single-row API each bulk kind loops over (de-order-service routes/api/order/*.ts); there is no bulk endpoint for these. */
const CALL: Record<BuyerBulkKind, (id: string) => Promise<unknown>> = {
  approve: (id) => OrderApi.respond(id, { approve: true }), // PUT /ord/order/:id/respond
  reject: (id) => OrderApi.respond(id, { approve: false }), // PUT /ord/order/:id/respond
  cancel: (id) => OrderApi.cancel(id), // PUT /ord/order/:id/cancel
  review: (id) => OrderApi.review(id, { review: true }), // PUT /ord/order/:id/review
}

const TITLE: Record<BuyerBulkKind, string> = {
  approve: "order:actions.approve",
  reject: "order:actions.reject",
  cancel: "common:actions.cancel",
  review: "order:actions.review",
}

const CONFIRM: Record<BuyerBulkKind, string> = {
  approve: "order:bulk.approveConfirm",
  reject: "order:bulk.rejectConfirm",
  cancel: "order:bulk.cancelConfirm",
  review: "order:bulk.reviewConfirm",
}

const SUCCESS: Record<BuyerBulkKind, string> = {
  approve: "order:buyerSales.approveSuccess",
  reject: "order:buyerSales.rejectSuccess",
  cancel: "order:buyerSales.bulkCancelSuccess",
  review: "order:buyerSales.reviewApprovedSuccess",
}

type Props = Readonly<{
  kind: BuyerBulkKind
  /** Only the selected rows the action applies to. */
  orders: IOrderPlus[]
  onCancel: () => void
  reload: () => void
  clearSelection: () => void
  /** Lets the page keep the modal open while the loop runs (Esc / outside click would orphan it). */
  onBusyChange?: (busy: boolean) => void
}>

/** Buyer list bulk dialog: the row's action applied to each selected order one by one; a partial failure is reported by order code. */
export function BuyerBulkActionForm({ kind, orders, onCancel, reload, clearSelection, onBusyChange }: Props) {
  const { t } = useTranslation(["order", "common"])
  const [loading, setLoading] = useState(false)

  const cancel = () => {
    if (!loading) onCancel()
  }

  const onSubmit = async () => {
    if (loading) return
    setLoading(true)
    onBusyChange?.(true)
    const failed: string[] = []
    let lastError: unknown
    try {
      for (const o of orders) {
        try {
          await CALL[kind](o.id!)
        } catch (err) {
          lastError = err
          failed.push(o.salesCode || o.purchaseCode || o.id || "-")
        }
      }
      const done = orders.length - failed.length
      if (failed.length === 0) message.success(t(SUCCESS[kind], { count: done }))
      else if (orders.length === 1) message.error(translateBackendError(lastError))
      else message.error(t("order:bulk.partial", { done, failed: failed.length, codes: failed.join(", ") }))
      clearSelection()
      reload()
    } finally {
      setLoading(false)
      onBusyChange?.(false)
    }
    onCancel()
  }

  return (
    <OrderActionCard
      title={t(TITLE[kind])}
      onClose={cancel}
      loading={loading}
      footer={
        <>
          <Button variant="default" onClick={cancel}>{t("common:actions.dismiss")}</Button>
          <Button color={kind === "reject" || kind === "cancel" ? "gray" : "brand"} variant={kind === "reject" || kind === "cancel" ? "light" : "filled"} onClick={onSubmit}>
            {t(TITLE[kind])}
          </Button>
        </>
      }
    >
      <Text fz={14}>{t(CONFIRM[kind], { count: orders.length })}</Text>
    </OrderActionCard>
  )
}
